import { useTranslation } from 'react-i18next';
import {
  Shield,
  Star,
  CheckCircle,
  Clock,
  type LucideIcon,
} from 'lucide-react';
import { SearchBar } from '@/components/search/SearchBar';
import type { ServiceType } from '@/types';

interface ServiceHeroProps {
  serviceType: ServiceType;
  icon: LucideIcon;
  gradient: string;
  title?: string;
  subtitle?: string;
  stats?: {
    stat1?: string;
    stat2?: string;
    stat3?: string;
  };
  showSearch?: boolean;
}

export function ServiceHero({
  serviceType,
  icon: Icon,
  gradient,
  title,
  subtitle,
  stats,
  showSearch = true,
}: ServiceHeroProps) {
  const { t } = useTranslation(['services', 'common']);

  const displayTitle = title || t(`services:${serviceType}.hero.title`);
  const displaySubtitle = subtitle || t(`services:${serviceType}.hero.subtitle`);
  const displayStats = stats || {
    stat1: t(`services:${serviceType}.hero.stat1`),
    stat2: t(`services:${serviceType}.hero.stat2`),
    stat3: t(`services:${serviceType}.hero.stat3`),
  };

  return (
    <section className={`relative bg-gradient-to-br ${gradient} text-white py-20 lg:py-32`}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Service Icon Badge */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-8 animate-fade-in">
            <Icon className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-fade-in">
            {displayTitle}
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 mb-10 max-w-3xl mx-auto animate-fade-in">
            {displaySubtitle}
          </p>

          {/* Search Bar */}
          {showSearch && (
            <div className="max-w-3xl mx-auto mb-10 animate-fade-in">
              <SearchBar variant="hero" initialServiceType={serviceType} />
            </div>
          )}

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-6 lg:gap-12 text-sm animate-fade-in">
            {displayStats.stat1 && (
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>{displayStats.stat1}</span>
              </div>
            )}
            {displayStats.stat2 && (
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                <span>{displayStats.stat2}</span>
              </div>
            )}
            {displayStats.stat3 && (
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-white/80" />
                <span>{displayStats.stat3}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
}
